import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, Col, Input, Row, Select } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import getCompanyIdFromToken from "../../utils/getCompanyIdFromToken";
import { getTemplateById, getTemplatesByCompanyId, saveTemplate } from "../../redux/rtk/features/letterTemplate/letterTemplateSlice";
import { getFieldsByCompanyId } from "../../redux/rtk/features/letterTemplate/letterTemplateFieldSlice";
import { getPlaceholderLabel, PLACEHOLDER_LABELS } from "./defaultLetterTemplates";
import PlaceholderSuggestTextArea from "./PlaceholderSuggestTextArea";
import "./LetterGeneration.css";

const LETTER_GENERATION_PATH = "/admin/hr/letter-generation";

export default function LetterTemplateForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const companyId = getCompanyIdFromToken();
  const templateList = useSelector((state) => state.letterTemplate?.list) || [];
  const fieldList = useSelector((state) => state.letterTemplateField?.list) || [];
  const loading = useSelector((state) => state.letterTemplate?.loading);

  const isEdit = id && id !== "new";
  const templateIdKey = (t) => t.id ?? t.Id ?? t.LetterTemplateId;

  const [form, setForm] = useState({
    name: "",
    type: "offer",
    content: "",
    annexureContent: "",
  });

  const placeholderSuggestionKeys = useMemo(
    () =>
      [
        ...new Set([
          ...Object.keys(PLACEHOLDER_LABELS || {}),
          ...(fieldList || []).map((f) => f.fieldKey ?? f.FieldKey).filter(Boolean),
        ]),
      ].sort((a, b) => a.localeCompare(b)),
    [fieldList]
  );

  useEffect(() => {
    dispatch(getTemplatesByCompanyId());
    dispatch(getFieldsByCompanyId());
  }, [dispatch]);

  useEffect(() => {
    if (!isEdit || !id) return;
    const existing = templateList.find(
      (t) => templateIdKey(t) === id || templateIdKey(t) === Number(id)
    );
    if (existing) {
      setForm({
        name: existing.name ?? existing.TemplateName ?? "",
        type: existing.type ?? existing.LetterType ?? "offer",
        content: existing.content ?? existing.TemplateContent ?? "",
        annexureContent: existing.annexureContent ?? existing.AnnexureContent ?? "",
      });
    } else {
      dispatch(getTemplateById(id))
        .unwrap()
        .then((payload) => {
          if (payload) {
            setForm({
              name: payload.name ?? payload.TemplateName ?? "",
              type: payload.type ?? payload.LetterType ?? "offer",
              content: payload.content ?? payload.TemplateContent ?? "",
              annexureContent: payload.annexureContent ?? payload.AnnexureContent ?? "",
            });
          }
        })
        .catch(() => {});
    }
  }, [id, isEdit, templateList, dispatch]);

  const handleSave = () => {
    if (!form.name?.trim()) {
      toast.error("Please enter template name");
      return;
    }
    if (!form.content?.trim()) {
      toast.error("Please enter main letter content");
      return;
    }
    const payload = {
      id: isEdit ? Number(id) || id : 0,
      LetterTemplateId: isEdit ? Number(id) || id : 0,
      name: form.name.trim(),
      type: form.type,
      content: form.content,
      annexureContent: form.annexureContent ?? "",
      companyId: companyId ?? 0,
    };
    dispatch(saveTemplate(payload))
      .unwrap()
      .then(() => {
        navigate(LETTER_GENERATION_PATH);
      })
      .catch(() => {});
  };

  const handleCancel = () => {
    navigate(LETTER_GENERATION_PATH);
  };

  return (
    <div className="letter-generation-page">
      <Card
        title={
          <span>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={handleCancel}
              style={{ marginRight: 8 }}
            />
            {isEdit ? "Edit letter template" : "Add letter template"}
          </span>
        }
        loading={loading}
      >
        <div >
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={14}>
              <label>Template name</label>
              <Input
                placeholder="e.g. Offer Letter (Default)"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                style={{ marginTop: 4 }}
              />
            </Col>
            <Col xs={24} sm={10}>
              <label>Letter type</label>
              <Select
                value={form.type}
                onChange={(v) => setForm((p) => ({ ...p, type: v }))}
                options={[
                  { value: "offer", label: "Offer Letter" },
                  { value: "appointment", label: "Appointment Letter" },
                ]}
                style={{ width: "100%", marginTop: 4 }}
              />
            </Col>
          </Row>
          <div style={{ marginBottom: 16 }}>
            <label>Paste your letter content</label>
            <p className="letter-hint" style={{ marginTop: 2, marginBottom: 4 }}>
              Type <code>[</code> or <code>[&#123;</code> to see placeholder suggestions.
            </p>
            <PlaceholderSuggestTextArea
              placeholder="Paste or type. Type [ for suggestions. Example: Dear [Candidate_Name], We are pleased to offer you [Job_Title]..."
              value={form.content}
              onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))}
              suggestionKeys={placeholderSuggestionKeys}
              getLabel={getPlaceholderLabel}
              rows={14}
              style={{ marginTop: 4, fontFamily: "monospace", fontSize: 12, minHeight: 320 }}
            />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label>Annexure (optional)</label>
            <p className="letter-hint" style={{ marginTop: 2, marginBottom: 4 }}>
              Type <code>[</code> for placeholder suggestions.
            </p>
            <PlaceholderSuggestTextArea
              placeholder="Paste annexure content. Type [ for suggestions..."
              value={form.annexureContent}
              onChange={(e) => setForm((p) => ({ ...p, annexureContent: e.target.value }))}
              suggestionKeys={placeholderSuggestionKeys}
              getLabel={getPlaceholderLabel}
              rows={10}
              style={{ marginTop: 4, fontFamily: "monospace", fontSize: 12, minHeight: 200 }}
            />
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <Button type="primary" onClick={handleSave}>
              Save template
            </Button>
            <Button onClick={handleCancel}>Cancel</Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
